import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/ui/Modal';
import toast from 'react-hot-toast';
import { Plus, Trash2, Search, Video, FolderPlus, ChevronDown } from 'lucide-react';

export default function Videos() {
  const api = useApi();
  const { can } = useAuth();
  const [cats, setCats] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('videos'); // 'videos' | 'categories'
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ category: '', mozo: '', title: '', link: '' });
  const [catForm, setCatForm] = useState({ title: '', mozos: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  async function load() {
    setLoading(true);
    const [cv, cc] = await Promise.all([api.get('/videos'), api.get('/categories')]);
    if (cv) setVideos(cv.videos || []);
    if (cc) setCats(cc.categories || []);
    setLoading(false);
  }

  async function addVideo() {
    if (!form.title.trim() || !form.link.trim()) return toast.error('Title aur link zaroori hain');
    setSaving(true);
    const d = await api.post('/videos', form);
    if (d?.video) { toast.success('Video add ho gayi!'); load(); setModal(null); }
    else toast.error(d?.error || 'Error');
    setSaving(false);
  }

  async function addCat() {
    if (!catForm.title.trim()) return toast.error('Category title likhein');
    setSaving(true);
    const mozos = catForm.mozos.split('\n').map(m => m.trim()).filter(Boolean);
    const d = await api.post('/categories', { title: catForm.title, mozos });
    if (d?.category) { toast.success('Category add ho gayi!'); load(); setModal(null); }
    else toast.error(d?.error || 'Error');
    setSaving(false);
  }

  async function delVideo(v) {
    if (!window.confirm(`"${v.title}" delete karein?`)) return;
    const d = await api.del('/videos/' + v.id);
    if (d?.success) { toast.success('Delete ho gayi'); load(); }
  }

  async function delCat(c) {
    if (!window.confirm(`"${c.title}" delete karein?`)) return;
    const d = await api.del('/categories/' + c.id);
    if (d?.success) { toast.success('Delete ho gayi'); load(); }
  }

  const selectedCatMozos = cats.find(c => c.title === form.category);
  const mozosArr = selectedCatMozos ? JSON.parse(selectedCatMozos.mozos || '[]') : [];

  const filteredVideos = videos.filter(v =>
    v.title?.toLowerCase().includes(search.toLowerCase()) ||
    v.category?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="page">
      <div className="page-header">
        <div className="section-title">🎥 Videos</div>
        <div className="section-sub">Bot ke video categories aur mozos manage karein</div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {['videos', 'categories'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 500, borderBottom: tab === t ? '2px solid var(--emerald-400)' : '2px solid transparent',
            color: tab === t ? 'var(--emerald-300)' : 'var(--text-muted)', marginBottom: -1,
            textTransform: 'capitalize', transition: 'all 0.15s'
          }}>
            {t === 'videos' ? '🎬 Videos' : '📁 Categories'}
          </button>
        ))}
      </div>

      {tab === 'videos' && (
        <>
          <div className="page-actions">
            <div className="search-wrap" style={{ flex: 1, maxWidth: 300 }}>
              <Search size={13} className="search-icon" />
              <input className="input" placeholder="Search karein..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            {can('videos') && (
              <button className="btn btn-primary" onClick={() => { setForm({ category: '', mozo: '', title: '', link: '' }); setModal('video'); }}>
                <Plus size={14} /> Nai Video
              </button>
            )}
          </div>
          <div className="card">
            {loading ? <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner" /></div> :
              filteredVideos.length === 0 ? <div className="empty"><div className="empty-icon"><Video size={36} /></div>Koi video nahi mili</div> : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Title</th><th>Category</th><th>Mozo</th><th>Link</th><th></th></tr></thead>
                    <tbody>
                      {filteredVideos.map(v => (
                        <tr key={v.id}>
                          <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{v.title}</td>
                          <td><span className="badge badge-green">{v.category}</span></td>
                          <td><span className="badge badge-gold">{v.mozo}</span></td>
                          <td>{v.link ? <a href={v.link} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>🔗 Link</a> : '—'}</td>
                          <td>{can('videos') && <button className="btn btn-icon btn-danger btn-sm" onClick={() => delVideo(v)}><Trash2 size={13} /></button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </>
      )}

      {tab === 'categories' && (
        <>
          <div className="page-actions">
            {can('videos') && (
              <button className="btn btn-primary" onClick={() => { setCatForm({ title: '', mozos: '' }); setModal('cat'); }}>
                <FolderPlus size={14} /> Nai Category
              </button>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {cats.map(c => (
              <div key={c.id} className="card card-hover">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14 }}>{c.title}</div>
                  {can('videos') && <button className="btn btn-icon btn-danger btn-sm" onClick={() => delCat(c)}><Trash2 size={12} /></button>}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {JSON.parse(c.mozos || '[]').map(m => (
                    <span key={m} className="badge badge-grey">{m}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {modal === 'video' && (
        <Modal title="🎬 Nai Video Add Karein" onClose={() => setModal(null)}
          footer={<>
            <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={addVideo} disabled={saving}>Add Karein</button>
          </>}
        >
          <div className="field">
            <label className="label">Category *</label>
            <select className="select" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value, mozo: '' }))}>
              <option value="">-- Category chunein --</option>
              {cats.map(c => <option key={c.id} value={c.title}>{c.title}</option>)}
            </select>
          </div>
          {form.category && (
            <div className="field">
              <label className="label">Mozo *</label>
              <select className="select" value={form.mozo} onChange={e => setForm(p => ({ ...p, mozo: e.target.value }))}>
                <option value="">-- Mozo chunein --</option>
                {mozosArr.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          )}
          <div className="field">
            <label className="label">Video Title *</label>
            <input className="input" placeholder="Video ka naam..." value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} />
          </div>
          <div className="field">
            <label className="label">YouTube Link *</label>
            <input className="input" placeholder="https://youtube.com/..." value={form.link} onChange={e => setForm(p => ({ ...p, link: e.target.value }))} />
          </div>
        </Modal>
      )}

      {modal === 'cat' && (
        <Modal title="📁 Nai Category Add Karein" onClose={() => setModal(null)}
          footer={<>
            <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={addCat} disabled={saving}>Add Karein</button>
          </>}
        >
          <div className="field">
            <label className="label">Category Title *</label>
            <input className="input" placeholder="Aqeedah & Tauheed..." value={catForm.title} onChange={e => setCatForm(p => ({ ...p, title: e.target.value }))} autoFocus />
          </div>
          <div className="field">
            <label className="label">Mozos (har mozo alag line mein)</label>
            <textarea className="textarea" placeholder={"Tauheed\nRisalat\nKhatm e Nubuwwat"} value={catForm.mozos} onChange={e => setCatForm(p => ({ ...p, mozos: e.target.value }))} style={{ minHeight: 120 }} />
          </div>
        </Modal>
      )}
    </div>
  );
}